import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight, ChevronDown, Upload, Download, Trash2, AlertTriangle,
  FileText, Search, Folder, ExternalLink, Paperclip, X, Wand2,
  Eye, EyeOff,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { type CoretelUser } from "@/lib/auth";
import { Card, Pill, Button, Spinner, Select, Input } from "@/components/ui";
import { fmtDate } from "@/lib/dates";

// =============================================================================
// LibraryDocumentsTab — T-161 Phase 2 (Slice 2)
// =============================================================================
// Replaces the flat DOC_TYPES dropdown with a two-tier hierarchical library:
//   Main category (8 pillars: Design / SAED / Compliance / Commercial / Site /
//   Acceptance / Admin / Reference)
//     └─ Subcategory (47 total) — each with read_roles + write_roles arrays
//        └─ Documents
//
// - Reads document_main_categories + document_subcategories from the DB so we
//   never duplicate the seed list in the UI. Adding a subcategory in SQL
//   shows up here on next refetch — no code change needed.
// - Filters subcategories by current user's role using read_roles/write_roles.
// - Upload form: main → sub two-dropdown picker, writes the new
//   main_category_id + subcategory_id columns on bid_documents (mig 111).
// - Legacy doc_type-only docs (where subcategory_id is null) bucket into
//   an "Uncategorised" group at the bottom — manager re-files via "Move".
// - Reference > MOPs is_external_link → no upload, opens /mops in new tab.
// - Acceptance > "Submitted to client" is_auto_populated → upload disabled,
//   docs land via T-160 HOP submission flow.
// =============================================================================

type MainCat = {
  main_category_id: string;
  code: string;
  display_name: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
};

type SubCat = {
  subcategory_id: string;
  main_category_id: string;
  code: string;
  display_name: string;
  description: string | null;
  read_roles: string[];
  write_roles: string[];
  move_roles: string[];
  sort_order: number;
  is_active: boolean;
  is_external_link: boolean;
  external_link_path: string | null;
  is_auto_populated: boolean;
};

type Doc = {
  id: string;
  bid_id: string | null;
  project_id: string | null;
  file_name: string;
  file_path: string;
  file_size_bytes: number | null;
  mime_type: string | null;
  doc_type: string | null;
  title: string | null;
  uploaded_at: string;
  main_category_id: string | null;
  subcategory_id: string | null;
};

export default function LibraryDocumentsTab({
  project,
  user,
  canEdit,
}: {
  project: any;
  user: CoretelUser | null;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const role = user?.role ?? "field";

  // ---------- Data ----------
  const { data: mainCats = [] } = useQuery<MainCat[]>({
    queryKey: ["doc_main_categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("document_main_categories")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as MainCat[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: subCats = [] } = useQuery<SubCat[]>({
    queryKey: ["doc_subcategories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("document_subcategories")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as SubCat[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: docs = [], isLoading: docsLoading } = useQuery<Doc[]>({
    queryKey: ["project_library_docs", project.project_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bid_documents")
        .select("id, bid_id, project_id, file_name, file_path, file_size_bytes, mime_type, doc_type, title, uploaded_at, main_category_id, subcategory_id")
        .eq("project_id", project.project_id)
        .order("uploaded_at", { ascending: false });
      if (error) throw error;
      return data as Doc[];
    },
  });

  // ---------- Role-filtered visibility ----------
  const visibleSubCats = useMemo(
    () => subCats.filter(s => s.read_roles.includes(role)),
    [subCats, role]
  );
  const visibleSubIds = useMemo(
    () => new Set(visibleSubCats.map(s => s.subcategory_id)),
    [visibleSubCats]
  );

  // ---------- Search + show-empty toggle ----------
  const [search, setSearch] = useState("");
  const searchLc = search.trim().toLowerCase();
  // Default-collapsed view: empty mains + empty subs are hidden so the tree
  // only shows rows that have content. Manager+ can flip it on to audit the
  // full taxonomy / find an empty home to upload into. Reference > MOPs
  // (external link) and Acceptance > Submitted to client (auto-populated)
  // stay visible regardless because they're meaningful placeholders.
  const [showAllEmpty, setShowAllEmpty] = useState(false);

  const filteredDocs = useMemo(() => {
    if (!searchLc) return docs;
    return docs.filter(d => {
      const fn = d.file_name?.toLowerCase() ?? "";
      const sub = subCats.find(s => s.subcategory_id === d.subcategory_id);
      const subName = sub?.display_name.toLowerCase() ?? "";
      return fn.includes(searchLc) || subName.includes(searchLc);
    });
  }, [docs, searchLc, subCats]);

  // Group docs by subcategory_id (or "uncategorised" bucket)
  const docsBySub = useMemo(() => {
    const m = new Map<string, Doc[]>();
    for (const d of filteredDocs) {
      const key = d.subcategory_id ?? "__uncat__";
      const arr = m.get(key) ?? [];
      arr.push(d);
      m.set(key, arr);
    }
    return m;
  }, [filteredDocs]);

  // For the role-filter, only count docs whose sub is visible
  const docCountForMain = (mainId: string) => {
    return visibleSubCats
      .filter(s => s.main_category_id === mainId)
      .reduce((acc, s) => acc + (docsBySub.get(s.subcategory_id)?.length ?? 0), 0);
  };

  // ---------- Expand/collapse state ----------
  // Default: expand main cats that have docs OR if a search is active, expand all
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const isOpen = (id: string) => {
    if (searchLc) return true;
    if (id in expanded) return expanded[id];
    return docCountForMain(id) > 0;
  };
  const toggle = (id: string) =>
    setExpanded(prev => ({ ...prev, [id]: !isOpen(id) }));

  // ---------- Upload ----------
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadMainId, setUploadMainId] = useState<string>("");
  const [uploadSubId, setUploadSubId] = useState<string>("");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadableSubs = useMemo(
    () => subCats.filter(s =>
      s.write_roles.includes(role) &&
      !s.is_external_link &&
      !s.is_auto_populated
    ),
    [subCats, role]
  );
  const uploadableMainIds = useMemo(
    () => new Set(uploadableSubs.map(s => s.main_category_id)),
    [uploadableSubs]
  );
  const uploadMains = useMemo(
    () => mainCats.filter(m => uploadableMainIds.has(m.main_category_id)),
    [mainCats, uploadableMainIds]
  );
  const uploadSubsForMain = useMemo(
    () => uploadableSubs.filter(s => s.main_category_id === uploadMainId),
    [uploadableSubs, uploadMainId]
  );

  // When main changes, reset sub
  function handleMainChange(id: string) {
    setUploadMainId(id);
    const first = uploadableSubs.find(s => s.main_category_id === id);
    setUploadSubId(first?.subcategory_id ?? "");
  }

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    if (!uploadSubId) {
      setUploadError("Pick a category and subcategory first.");
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      const sub = subCats.find(s => s.subcategory_id === uploadSubId);
      if (!sub) throw new Error("Subcategory not found.");
      const main = mainCats.find(m => m.main_category_id === sub.main_category_id);

      const pathPrefix = project.source_bid_id
        ? project.source_bid_id
        : `project-${project.project_id}`;

      for (const file of Array.from(files)) {
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        const safeName = file.name.replace(/[^A-Za-z0-9._-]/g, "_");
        const path = `${pathPrefix}/${ts}-${safeName}`;

        const { error: upErr } = await supabase.storage
          .from("bid-documents")
          .upload(path, file, { contentType: file.type || "application/octet-stream", upsert: false });
        if (upErr) throw upErr;

        // doc_type kept populated with main+sub code for legacy compat /
        // search fallbacks. main_category_id + subcategory_id are the
        // authoritative columns going forward.
        const legacyDocType = main && sub
          ? `${main.code}.${sub.code}`.toLowerCase()
          : sub.code.toLowerCase();

        const { error: insErr } = await supabase.from("bid_documents").insert({
          bid_id:           project.source_bid_id ?? null,
          project_id:       project.project_id,
          file_name:        file.name,
          file_path:        path,
          file_size_bytes:  file.size,
          mime_type:        file.type || null,
          doc_type:         legacyDocType,
          main_category_id: sub.main_category_id,
          subcategory_id:   sub.subcategory_id,
        });
        if (insErr) throw insErr;
      }
      qc.invalidateQueries({ queryKey: ["project_library_docs", project.project_id] });
      setUploadOpen(false);
    } catch (e: any) {
      setUploadError(e?.message ?? String(e));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  // ---------- Move (re-categorise) ----------
  const moveMut = useMutation({
    mutationFn: async ({ doc, newSubId }: { doc: Doc; newSubId: string }) => {
      const sub = subCats.find(s => s.subcategory_id === newSubId);
      if (!sub) throw new Error("Subcategory not found.");
      const { error } = await supabase
        .from("bid_documents")
        .update({
          main_category_id: sub.main_category_id,
          subcategory_id:   sub.subcategory_id,
        })
        .eq("id", doc.id);
      if (error) throw error;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["project_library_docs", project.project_id] }),
  });

  // ---------- Delete ----------
  const deleteMut = useMutation({
    mutationFn: async (doc: Doc) => {
      const { error: sErr } = await supabase.storage.from("bid-documents").remove([doc.file_path]);
      if (sErr) throw sErr;
      const { error: dErr } = await supabase.from("bid_documents").delete().eq("id", doc.id);
      if (dErr) throw dErr;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["project_library_docs", project.project_id] }),
  });

  async function handleDownload(doc: Doc) {
    // iOS Safari requires window.open synchronously inside the user-gesture
    // handler. After the await on createSignedUrl(), the popup gets blocked.
    // Open a blank window NOW, navigate it once we have the URL.
    // 9 Sep: "noopener" makes window.open return NULL — tab opened but could
    // never be navigated (blank page on phones). No features = handle back;
    // sever opener by hand.
    const win = window.open("", "_blank");
    if (win) win.opener = null;
    const { data, error } = await supabase.storage
      .from("bid-documents")
      .createSignedUrl(doc.file_path, 60 * 10);
    if (error || !data?.signedUrl) {
      if (win) win.close();
      alert(error?.message ?? "Could not load document");
      return;
    }
    const mime = doc.mime_type ?? "";
    const inlineable = mime === "application/pdf" || mime.startsWith("image/");
    const url = inlineable
      ? `${data.signedUrl}&response-content-disposition=inline`
      : data.signedUrl;
    if (win) win.location.href = url;
    else window.location.href = url;  // popup blocked entirely — same-tab fallback
  }

  // ---------- Render ----------
  const totalVisibleDocs = useMemo(() => {
    return Array.from(docsBySub.entries()).reduce((acc, [subId, arr]) => {
      if (subId === "__uncat__") return acc + arr.length;
      return visibleSubIds.has(subId) ? acc + arr.length : acc;
    }, 0);
  }, [docsBySub, visibleSubIds]);

  const uncatDocs = docsBySub.get("__uncat__") ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <div className="p-5">
          {/* Header row */}
          <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Paperclip size={15} className="text-text-muted"/>
              <h3 className="text-[15px] font-semibold tracking-tight">
                Project documents
              </h3>
              <span className="text-[11px] font-mono text-text-muted">
                ({totalVisibleDocs})
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {project.source_bid_id && (
                <Link to={`/bids/${project.source_bid_id}`}
                  className="text-[11px] text-accent hover:underline inline-flex items-center gap-1">
                  manage via source BID <ExternalLink size={10}/>
                </Link>
              )}
              {canEdit && (
                <Button
                  size="sm"
                  icon={Upload}
                  onClick={() => {
                    setUploadOpen(true);
                    if (!uploadMainId && uploadMains[0]) {
                      handleMainChange(uploadMains[0].main_category_id);
                    }
                  }}
                >
                  Upload
                </Button>
              )}
            </div>
          </div>

          {/* Search + Show empty toggle */}
          <div className="mb-3 flex items-center gap-2">
            <div className="flex-1 relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"/>
              <Input
                value={search}
                onChange={(e: any) => setSearch(e.target.value)}
                placeholder="Search by filename or category…"
                className="!pl-8 !text-[12px]"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-ink-hover text-text-muted"
                  title="Clear"
                >
                  <X size={12}/>
                </button>
              )}
            </div>
            <button
              onClick={() => setShowAllEmpty(v => !v)}
              className={`shrink-0 px-2.5 py-2 rounded border text-[11px] inline-flex items-center gap-1.5 transition-colors ${
                showAllEmpty
                  ? "border-accent/40 bg-accent/10 text-accent"
                  : "border-line bg-ink/40 text-text-muted hover:bg-ink-hover"
              }`}
              title={showAllEmpty ? "Hide empty categories" : "Show all categories (including empty)"}
            >
              {showAllEmpty ? <EyeOff size={11}/> : <Eye size={11}/>}
              {showAllEmpty ? "Hide empty" : "Show all"}
            </button>
          </div>

          {/* Library tree */}
          {docsLoading ? (
            <div className="py-6 flex justify-center"><Spinner/></div>
          ) : (
            <div className="space-y-2">
              {mainCats.map(main => {
                const mainSubs = visibleSubCats
                  .filter(s => s.main_category_id === main.main_category_id);
                if (mainSubs.length === 0) return null; // role can't see any sub

                const count = docCountForMain(main.main_category_id);
                const hasMeaningfulPlaceholders = mainSubs.some(s => s.is_external_link);
                const open = isOpen(main.main_category_id);

                // Hide rules:
                //   - searching and no match → always hide
                //   - default view (showAllEmpty off) and zero docs → hide,
                //     unless the main contains an external-link sub (e.g.
                //     Reference > MOPs) which is always meaningful.
                if (searchLc && count === 0) return null;
                if (!showAllEmpty && !searchLc && count === 0 && !hasMeaningfulPlaceholders) return null;

                return (
                  <div
                    key={main.main_category_id}
                    className={`border rounded-lg overflow-hidden ${count > 0 ? "border-line" : "border-line/60"}`}
                  >
                    <button
                      onClick={() => toggle(main.main_category_id)}
                      className={`w-full px-4 py-3.5 hover:bg-ink-hover flex items-center gap-3 text-left transition-colors ${
                        count > 0 ? "bg-ink-card" : "bg-ink/30"
                      }`}
                    >
                      {open
                        ? <ChevronDown size={16} className="text-text-muted"/>
                        : <ChevronRight size={16} className="text-text-muted"/>}
                      <Folder size={16} className={count > 0 ? "text-accent" : "text-text-muted"}/>
                      <span className="text-[15px] font-semibold tracking-tight">{main.display_name}</span>
                      <span className={`ml-auto text-[12px] font-mono px-2 py-0.5 rounded ${
                        count > 0 ? "bg-accent/10 text-accent" : "text-text-dim"
                      }`}>
                        {count}
                      </span>
                    </button>

                    {open && (
                      <div className="divide-y divide-line/60">
                        {mainSubs.map(sub => {
                          const subDocs = docsBySub.get(sub.subcategory_id) ?? [];
                          // Hide empty subs unless: searching for it, toggle on,
                          // external link, or auto-populated (meaningful empty).
                          if (searchLc && subDocs.length === 0) return null;
                          if (
                            !showAllEmpty &&
                            !searchLc &&
                            subDocs.length === 0 &&
                            !sub.is_external_link &&
                            !sub.is_auto_populated
                          ) return null;
                          return (
                            <SubcategoryRow
                              key={sub.subcategory_id}
                              sub={sub}
                              docs={subDocs}
                              role={role}
                              canEdit={canEdit}
                              onDownload={handleDownload}
                              onDelete={(d) => {
                                if (confirm(`Delete ${d.file_name}?`)) deleteMut.mutate(d);
                              }}
                              onMove={(d, newSubId) => moveMut.mutate({ doc: d, newSubId })}
                              moveTargets={subCats.filter(s2 =>
                                s2.move_roles.includes(role) &&
                                !s2.is_external_link &&
                                !s2.is_auto_populated &&
                                s2.subcategory_id !== sub.subcategory_id
                              )}
                              mainCats={mainCats}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Uncategorised bucket — only for legacy docs */}
              {uncatDocs.length > 0 && (
                <div className="border border-line rounded-lg overflow-hidden">
                  <div className="px-3 py-2.5 bg-ink/40 flex items-center gap-2">
                    <AlertTriangle size={13} className="text-warning"/>
                    <span className="text-[13px] font-semibold">Uncategorised</span>
                    <span className="text-[10.5px] text-text-dim">
                      legacy uploads — manager can re-file using Move
                    </span>
                    <span className="ml-auto text-[10.5px] font-mono text-text-muted">
                      {uncatDocs.length}
                    </span>
                  </div>
                  <div className="divide-y divide-line/60">
                    {uncatDocs.map(d => (
                      <DocRow
                        key={d.id}
                        doc={d}
                        legacyTypeLabel={d.doc_type ?? "—"}
                        canEdit={canEdit}
                        canMove={canEdit}
                        moveTargets={subCats.filter(s2 =>
                          s2.move_roles.includes(role) &&
                          !s2.is_external_link &&
                          !s2.is_auto_populated
                        )}
                        mainCats={mainCats}
                        onDownload={() => handleDownload(d)}
                        onDelete={() => {
                          if (confirm(`Delete ${d.file_name}?`)) deleteMut.mutate(d);
                        }}
                        onMove={(newSubId) => moveMut.mutate({ doc: d, newSubId })}
                      />
                    ))}
                  </div>
                </div>
              )}

              {totalVisibleDocs === 0 && uncatDocs.length === 0 && !searchLc && !showAllEmpty && (
                <div className="py-8 text-center">
                  <FileText size={24} className="text-text-dim mx-auto mb-2"/>
                  <div className="text-[12px] text-text-muted">
                    No documents on this project yet.
                  </div>
                  {canEdit && (
                    <div className="text-[11px] text-text-dim mt-1">
                      Click <span className="font-mono">Upload</span> above, or toggle <span className="font-mono">Show all</span> to browse the empty taxonomy.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {project.sharepoint_folder_url && (
        <a href={project.sharepoint_folder_url} target="_blank" rel="noreferrer" className="inline-block">
          <Button icon={ExternalLink} variant="secondary">Open SharePoint folder (legacy)</Button>
        </a>
      )}

      {/* Upload modal */}
      {uploadOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => !uploading && setUploadOpen(false)}>
          <div className="bg-ink-card border border-line rounded-xl shadow-xl max-w-md w-full"
            onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-line flex items-center justify-between">
              <h3 className="text-[15px] font-semibold">Upload document</h3>
              <button
                onClick={() => !uploading && setUploadOpen(false)}
                className="p-1 rounded hover:bg-ink-hover text-text-muted"
              >
                <X size={14}/>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-[11px] text-text-muted mb-1">Main category</label>
                <Select
                  value={uploadMainId}
                  onChange={(e: any) => handleMainChange(e.target.value)}
                  className="!text-[12px]"
                >
                  {uploadMains.map(m => (
                    <option key={m.main_category_id} value={m.main_category_id}>
                      {m.display_name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="block text-[11px] text-text-muted mb-1">Subcategory</label>
                <Select
                  value={uploadSubId}
                  onChange={(e: any) => setUploadSubId(e.target.value)}
                  className="!text-[12px]"
                  disabled={uploadSubsForMain.length === 0}
                >
                  {uploadSubsForMain.map(s => (
                    <option key={s.subcategory_id} value={s.subcategory_id}>
                      {s.display_name}
                    </option>
                  ))}
                </Select>
                {(() => {
                  const sub = subCats.find(s => s.subcategory_id === uploadSubId);
                  if (!sub?.description) return null;
                  return (
                    <div className="text-[10.5px] text-text-dim mt-1">{sub.description}</div>
                  );
                })()}
              </div>

              <label
                className={`block rounded border border-dashed border-line hover:border-accent/40 bg-ink/40 px-4 py-6 text-center cursor-pointer transition-colors ${uploading ? "opacity-60 cursor-wait" : ""}`}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleFiles(e.dataTransfer.files); }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  disabled={uploading || !uploadSubId}
                  onChange={(e) => handleFiles(e.target.files)}
                />
                <Upload size={16} className="text-text-muted mx-auto mb-1.5"/>
                <div className="text-[12px] text-text-muted">
                  {uploading ? "Uploading…" : "Drop files here or click to browse"}
                </div>
                <div className="text-[10px] text-text-dim mt-1">
                  PDF, DWG, images, Word — up to 50 MB each
                </div>
              </label>

              {uploadError && (
                <div className="bg-danger/10 border border-danger/20 rounded px-3 py-2 text-[11.5px] text-danger">
                  <AlertTriangle size={11} className="inline mr-1"/>
                  {uploadError}
                </div>
              )}

              <div className="text-[10.5px] text-text-dim flex items-start gap-1.5 pt-1">
                <Wand2 size={11} className="mt-0.5 shrink-0"/>
                <span>Smart auto-categorisation coming soon — for now, pick the right home.</span>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-line bg-ink/40 rounded-b-xl flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setUploadOpen(false)}
                disabled={uploading}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SubcategoryRow — header + child docs (collapsible)
// =============================================================================
function SubcategoryRow({
  sub, docs, role, canEdit, onDownload, onDelete, onMove, moveTargets, mainCats,
}: {
  sub: SubCat;
  docs: Doc[];
  role: string;
  canEdit: boolean;
  onDownload: (d: Doc) => void;
  onDelete: (d: Doc) => void;
  onMove: (d: Doc, newSubId: string) => void;
  moveTargets: SubCat[];
  mainCats: MainCat[];
}) {
  const canWriteHere = sub.write_roles.includes(role);
  // Default open if the sub has any docs, otherwise collapsed
  const [open, setOpen] = useState(docs.length > 0);

  // External link sub — no doc list, just link out
  // Renders as a filled accent-tinted band so it stands out as a navigable
  // tile rather than a row that "looks like" a doc subcategory.
  if (sub.is_external_link) {
    return (
      <a
        href={sub.external_link_path ?? "#"}
        target={sub.external_link_path?.startsWith("http") ? "_blank" : undefined}
        rel="noreferrer"
        className="block pl-10 pr-4 py-2.5 bg-accent/8 border-l-2 border-l-accent/60 hover:bg-accent/12 transition-colors flex items-center gap-2"
      >
        <ExternalLink size={12} className="text-accent"/>
        <span className="text-[13px] font-medium text-text">{sub.display_name}</span>
        {sub.description && (
          <span className="text-[10.5px] text-text-dim">— {sub.description}</span>
        )}
        <span className="ml-auto text-[10.5px] font-medium text-accent">opens →</span>
      </a>
    );
  }

  // Regular subcategory row.
  // - Has docs:  filled accent-tinted band, left accent rule, bolder text.
  // - No docs:   subtle plain row (only shown when "Show all" toggle is on).
  // - Auto-populated empty: same as no-docs but with the `auto` pill.
  const hasDocs = docs.length > 0;
  const headerClass = hasDocs
    ? "w-full pl-10 pr-4 py-2.5 bg-accent/8 border-l-2 border-l-accent/60 hover:bg-accent/12 flex items-center gap-2 text-left transition-colors"
    : "w-full pl-10 pr-4 py-2 bg-ink/20 hover:bg-ink-hover flex items-center gap-2 text-left transition-colors";

  return (
    <div>
      <button onClick={() => setOpen(o => !o)} className={headerClass}>
        {hasDocs
          ? (open
              ? <ChevronDown size={12} className="text-accent"/>
              : <ChevronRight size={12} className="text-accent"/>)
          : <span className="w-[12px]"/>}
        <span className={hasDocs ? "text-[13px] font-semibold text-text" : "text-[12.5px] text-text-muted"}>
          {sub.display_name}
        </span>
        {sub.is_auto_populated && (
          <Pill label="auto" tone="bid" small />
        )}
        {!canWriteHere && (
          <span className="text-[10px] text-text-dim">read-only</span>
        )}
        <span className={`ml-auto text-[11px] font-mono px-1.5 py-0.5 rounded ${
          hasDocs ? "bg-accent/15 text-accent font-medium" : "text-text-dim"
        }`}>
          {docs.length}
        </span>
      </button>
      {open && hasDocs && (
        <div className="bg-ink/30 border-l-2 border-l-accent/20">
          {docs.map(d => (
            <DocRow
              key={d.id}
              doc={d}
              canEdit={canEdit && canWriteHere}
              canMove={canEdit && sub.move_roles.includes(role)}
              moveTargets={moveTargets}
              mainCats={mainCats}
              onDownload={() => onDownload(d)}
              onDelete={() => onDelete(d)}
              onMove={(newSubId) => onMove(d, newSubId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// DocRow — single document row (download / move / delete)
// =============================================================================
function DocRow({
  doc, canEdit, canMove, moveTargets, mainCats, onDownload, onDelete, onMove,
  legacyTypeLabel,
}: {
  doc: Doc;
  canEdit: boolean;
  canMove: boolean;
  moveTargets: SubCat[];
  mainCats: MainCat[];
  onDownload: () => void;
  onDelete: () => void;
  onMove: (newSubId: string) => void;
  legacyTypeLabel?: string;
}) {
  function fmtBytes(n: number | null | undefined): string {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n/1024).toFixed(0)} KB`;
    return `${(n/1024/1024).toFixed(1)} MB`;
  }

  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.storage
        .from("bid-documents")
        .createSignedUrl(doc.file_path, 60 * 10);
      if (cancelled || error || !data?.signedUrl) return;
      setSignedUrl(data.signedUrl);
    })();
    return () => { cancelled = true; };
  }, [doc.file_path]);

  // Group move targets by main category for the dropdown
  const groupedTargets = useMemo(() => {
    const m = new Map<string, SubCat[]>();
    for (const s of moveTargets) {
      const arr = m.get(s.main_category_id) ?? [];
      arr.push(s);
      m.set(s.main_category_id, arr);
    }
    return m;
  }, [moveTargets]);

  return (
    <div className="px-3 py-2 pl-12 flex items-center gap-3 border-t border-line/40 hover:bg-ink-hover/40">
      <FileText size={12} className="text-text-muted shrink-0"/>
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] text-text truncate">{doc.file_name}</div>
        <div className="text-[10px] font-mono text-text-muted">
          {fmtBytes(doc.file_size_bytes)} · uploaded {fmtDate(doc.uploaded_at)}
          {doc.bid_id && doc.project_id && (
            <span className="ml-2 text-text-dim">· carried from BID</span>
          )}
          {legacyTypeLabel && legacyTypeLabel !== "—" && (
            <span className="ml-2 text-text-dim">· {legacyTypeLabel}</span>
          )}
        </div>
      </div>
      {signedUrl ? (
        <>
          {/* View: Supabase serves the object inline by default, so the bare
              signed URL renders in the new tab (PDF/image preview on mobile). */}
          <a
            href={signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 rounded hover:bg-ink text-text-muted hover:text-text inline-flex items-center"
            title="Open in new tab"
            onClick={e => e.stopPropagation()}
          >
            <ExternalLink size={13}/>
          </a>
          {/* Download (desktop only): Supabase's &download=<name> query param
              sets Content-Disposition: attachment with the exact filename.
              The S3-style response-content-disposition param is NOT honored by
              Supabase Storage, which is why it previewed instead of downloading. */}
          <a
            href={`${signedUrl}&download=${encodeURIComponent(doc.file_name)}`}
            download={doc.file_name}
            className="hidden md:inline-flex p-1.5 rounded hover:bg-ink text-text-muted hover:text-text items-center"
            title="Download"
            onClick={e => e.stopPropagation()}
          >
            <Download size={13}/>
          </a>
        </>
      ) : (
        <button
          onClick={onDownload}
          className="p-1.5 rounded hover:bg-ink text-text-muted hover:text-text"
          title="Loading…"
        >
          <Download size={13} className="opacity-50"/>
        </button>
      )}
      {canMove && moveTargets.length > 0 && (
        <MoveDropdown
          mainCats={mainCats}
          groupedTargets={groupedTargets}
          onMove={onMove}
        />
      )}
      {canEdit && (
        <button
          onClick={onDelete}
          className="p-1.5 rounded hover:bg-ink text-text-muted hover:text-danger"
          title="Delete"
        >
          <Trash2 size={13}/>
        </button>
      )}
    </div>
  );
}

// =============================================================================
// MoveDropdown — viewport-aware menu that flips up when near the bottom of
// the page. Avoids the "last row in the list has its dropdown clipped by the
// viewport" issue. Measured on open via getBoundingClientRect; if there
// isn't ~280px of clearance below, render upward instead.
// =============================================================================
function MoveDropdown({
  mainCats, groupedTargets, onMove,
}: {
  mainCats: MainCat[];
  groupedTargets: Map<string, SubCat[]>;
  onMove: (newSubId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [flipUp, setFlipUp] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  function handleToggle() {
    if (!open && btnRef.current) {
      // Decide direction before showing — measure space below the trigger.
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      // Dropdown's max content height is 320px; flip up if we don't have
      // at least 280px below (gives room for trigger + a bit of padding).
      setFlipUp(spaceBelow < 280);
    }
    setOpen(o => !o);
  }

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={handleToggle}
        className="p-1.5 rounded hover:bg-ink text-text-muted hover:text-text text-[10.5px]"
        title="Move to another category"
      >
        Move
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            className={`absolute right-0 ${flipUp ? "bottom-full mb-1" : "top-full mt-1"} bg-ink-card border border-line rounded-lg shadow-lg min-w-[240px] max-h-[320px] overflow-y-auto z-40`}
          >
            {mainCats.map(m => {
              const subs = groupedTargets.get(m.main_category_id);
              if (!subs || subs.length === 0) return null;
              return (
                <div key={m.main_category_id} className="py-1">
                  <div className="px-3 py-1 text-[10px] font-mono uppercase text-text-dim border-b border-line/40">
                    {m.display_name}
                  </div>
                  {subs.map(s => (
                    <button
                      key={s.subcategory_id}
                      onClick={() => { setOpen(false); onMove(s.subcategory_id); }}
                      className="w-full text-left px-3 py-1.5 text-[11.5px] hover:bg-ink-raised"
                    >
                      {s.display_name}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
